import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Eye, ChevronUp, ChevronDown, ChevronsUpDown, AlertTriangle, Pencil, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { employeeApi, masterApi, documentApi, docReqApi, preJoinerApi, vacancyApi, ALL_UNITS } from '../lib/api';
import { SourceOfHiringFields, ExperienceFields, useHiringMeta } from '../components/HiringFields';
import { ageFrom, isBirthdayToday, daysUntilBirthday, departmentTree, inDepartment } from '../lib/utils';
import DocumentScanner from '../components/DocumentScanner';
import ConfirmationDuePanel from '../components/ConfirmationDuePanel';

const DOC_CHECKLIST = [
  '10th Marksheet', '12th Marksheet', 'Graduation (DMC)', 'PAN Card', 'Aadhar Card',
  'Experience Letter', 'Photo', 'PF / ESI Number', 'Relieving Letter', 'Offer Letter',
  'Salary Slip (Last 3 Months)', 'Bank Details',
];

// "15 Nov 2026" — a date HR can read at a glance, rather than an ISO string.
const dmy = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? String(iso).slice(0, 10)
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Serving notice, by the state the SERVER derived rather than by the
// `on_notice` flag alone. The flag is only written by the routes that know to
// set it, so a record carrying a last working day without one used to show as
// plain "Active" — the person looked like ordinary staff right up to the day
// they left. `employment_state` is the same answer the profile and the org
// chart use, which is what keeps the three of them agreeing.
const onNotice = (e) => (e?.employment_state === 'On Notice Period') || !!e?.on_notice;
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useUnit } from '../contexts/UnitContext';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

export default function Employees() {
  const { user, hasPerm } = useAuth();
  const { activeUnit } = useUnit();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterJoinYear, setFilterJoinYear] = useState('');
  const [filterDesig, setFilterDesig] = useState('');
  // Filtered in the browser alongside the existing ones, so these behave the
  // same as Department and Level and the whole bar clears together. The
  // server supports both as query params too, for anything reading the API.
  const [filterSource, setFilterSource] = useState('');
  const [filterExp, setFilterExp] = useState('');
  const [docSummary, setDocSummary] = useState({});
  const [docFilterOn, setDocFilterOn] = useState(false);
  const [searchParams] = useSearchParams();
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [hiringMeta, setHiringMeta] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // "Add Employee from Vacancies" — the same create form, opened against a
  // seat that is already open, so the role details come from the vacancy
  // rather than being typed in again.
  const [vacancyDialogOpen, setVacancyDialogOpen] = useState(false);
  const [openVacancies, setOpenVacancies] = useState([]);
  const [birthdayOpen, setBirthdayOpen] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  // Pending-documents panel: doc-type selector + list of employees who haven't uploaded that type.
  const [docReqs, setDocReqs] = useState([]);
  const [pendingDocType, setPendingDocType] = useState('');
  const [pendingList, setPendingList] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  useEffect(() => {
    docReqApi.list().then((r) => setDocReqs(r.data || [])).catch(() => setDocReqs([]));
  }, []);

  // The source/experience vocabularies. A failure here must not take the
  // whole page down — the columns and filters fall back to their defaults.
  useEffect(() => {
    employeeApi.hiringMeta().then((r) => setHiringMeta(r.data)).catch(() => setHiringMeta(null));
  }, []);

  useEffect(() => {
    if (!pendingDocType) { setPendingList([]); return; }
    setPendingLoading(true);
    documentApi.pending({ doc_type: pendingDocType, unit: activeUnit })
      .then((r) => setPendingList(r.data || []))
      .catch(() => setPendingList([]))
      .finally(() => setPendingLoading(false));
  }, [pendingDocType, activeUnit]);

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [activeUnit]);

  // Birthday greeting. Computed from the roster already loaded, so it costs no
  // extra request. Shown once per calendar day per browser — reopening the page
  // or switching units shouldn't nag.
  const birthdayPeople = useMemo(
    () => employees.filter((e) => e.status !== 'Left' && isBirthdayToday(e.dob)),
    [employees],
  );

  // Birthdays falling in the next 7 days — a 15 Aug birthday is listed from
  // 8 Aug through 14 Aug. The day itself is excluded; that person moves to the
  // "Today" row instead. Soonest first.
  const upcomingBirthdays = useMemo(() => (
    employees
      .filter((e) => e.status !== 'Left')
      .map((e) => ({ emp: e, days: daysUntilBirthday(e.dob) }))
      .filter((x) => x.days !== null && x.days >= 1 && x.days <= 7)
      .sort((a, b) => a.days - b.days || (a.emp.name || '').localeCompare(b.emp.name || ''))
  ), [employees]);

  useEffect(() => {
    if (birthdayPeople.length === 0) return;
    const today = new Date().toLocaleDateString('en-CA'); // local YYYY-MM-DD
    if (localStorage.getItem('birthdayNoticeSeen') === today) return;
    setBirthdayOpen(true);
    localStorage.setItem('birthdayNoticeSeen', today);
  }, [birthdayPeople]);

  useEffect(() => {
    setFilterDept(searchParams.get('department') || '');
    setFilterLevel(searchParams.get('level') || '');
    setFilterJoinYear(searchParams.get('joinYear') || '');
    setFilterDesig(searchParams.get('designation') || '');
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [empRes, unitsRes, deptsRes, desigsRes, docRes] = await Promise.all([
        employeeApi.list({ unit: activeUnit }), masterApi.units(), masterApi.departments(), masterApi.designations(),
        documentApi.summary({ unit: activeUnit }).catch(() => ({ data: {} })),
      ]);
      setEmployees(empRes.data); setUnits(unitsRes.data); setDepartments(deptsRes.data); setDesignations(desigsRes.data);
      setDocSummary(docRes.data || {});
    } catch (error) { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
    // Open seats, for "Add Employee from Vacancies". Loaded alongside the
    // roster rather than on dialog open so the button can say how many there
    // are, and so it can hide itself when there are none.
    vacancyApi.list({ unit: activeUnit })
      .then((r) => setOpenVacancies((r.data || []).filter((v) => v.status === 'Open')))
      .catch(() => setOpenVacancies([]));
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const incompleteIds = useMemo(() => {
    const s = new Set();
    employees.forEach((e) => {
      const have = docSummary[e.id] || [];
      if (!DOC_CHECKLIST.every((t) => have.includes(t))) s.add(e.id);
    });
    return s;
  }, [employees, docSummary]);

  const filteredEmployees = useMemo(() => {
    const list = employees.filter((emp) => {
      if (docFilterOn && !incompleteIds.has(emp.id)) return false;
      if (search && !emp.name.toLowerCase().includes(search.toLowerCase())) return false;
      // A group (Quality) covers everything inside it — QA, QC, SQA, Standard Room.
      if (filterDept && !inDepartment(departments, filterDept, emp.department)) return false;
      if (filterLevel && String(emp.level) !== String(filterLevel)) return false;
      if (filterJoinYear && String(emp.year_join) !== String(filterJoinYear)) return false;
      if (filterDesig && emp.designation !== filterDesig) return false;
      if (filterSource) {
        // "Not Recorded" is a real choice — it is how HR finds the backlog
        // of people hired before the field existed.
        const got = emp.source_of_hiring || '';
        if (filterSource === 'Not Recorded' ? got : got !== filterSource) return false;
      }
      if (filterExp) {
        const t = emp.experience_type || '';
        if (filterExp === 'Not Recorded' ? t : t !== filterExp) return false;
      }
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (e) => {
      switch (sortKey) {
        case 'department': return (e.department || '').toLowerCase();
        case 'designation': return e.level || 0;                 // sort designation column by rank/level
        case 'doj': return e.doj ? new Date(e.doj).getTime() || 0 : 0;
        // Missing DOBs sort to the top so gaps are easy to find and fill.
        case 'dob': return e.dob ? new Date(e.dob).getTime() || 0 : 0;
        case 'locked_salary': return (e.locked_salary != null ? Number(e.locked_salary) : -1);
        case 'name':
        default: return (e.name || '').toLowerCase();
      }
    };
    return [...list].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return (a.name || '').localeCompare(b.name || '') * dir;
    });
  }, [employees, departments, search, filterDept, filterLevel, filterJoinYear, filterDesig,
      filterSource, filterExp, sortKey, sortDir, docFilterOn, incompleteIds]);

  // Nobody has to be moved on the day — the backend retires them from their
  // last working day. This is only so the count is visible while it matters.
  const noticeCount = useMemo(() => filteredEmployees.filter(onNotice).length,
                              [filteredEmployees]);
  // Seats somebody actually vacated — the only ones "Add from Vacancy" fills.
  // A position opened from scratch has nobody behind it to replace.
  const replacementSeats = useMemo(() => openVacancies.filter((v) => v.from_employee_name),
                                   [openVacancies]);
  const canCreateEmployee = hasPerm('employee.create');
  const showSalary = filteredEmployees.some((e) => e.locked_salary != null);
  const inrFmt = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);

  /* Not the same as empty. These two fields arrived after most of the roll
     was already entered, so "we never asked" is a real state and has to read
     as itself — showing a blank experience as "Fresher" would assert that a
     hundred people joined with none. */
  const NotRecorded = () => (
    <span className="text-[#C9CFDB] text-xs italic">Not Recorded</span>
  );

  const SortHead = ({ label, k, sortable = true }) => (
    <th className={`py-3 px-4 text-left ${sortable ? 'cursor-pointer select-none' : ''}`}
        onClick={sortable ? () => toggleSort(k) : undefined}
        data-testid={sortable ? `sort-${k}` : undefined}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sortable && (sortKey === k
          ? (sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)
          : <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />)}
      </span>
    </th>
  );

  return (
    <div data-testid="employees-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="min-w-0">
                <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-extrabold text-[#172554] mb-2">Employees</h1>
                <p className="text-sm text-[#5A6377]">
                  {filteredEmployees.length} employees found
                  {noticeCount > 0 && (
                    <span data-testid="notice-count" className="ml-2 text-[#8A5A12]">
                      · {noticeCount} on notice period
                    </span>
                  )}
                </p>
                {incompleteIds.size > 0 && (
                  <button data-testid="docs-incomplete-bubble" onClick={() => setDocFilterOn((v) => !v)}
                    className={`mt-2 inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${docFilterOn ? 'bg-[#E9A23B] text-white border-[#E9A23B]' : 'bg-[#FBF7EC] text-[#8A5A12] border-[#F0E2C0] hover:bg-[#F5EBD0]'}`}>
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {incompleteIds.size} employee{incompleteIds.size === 1 ? '' : 's'} with incomplete documents{docFilterOn ? ' · showing only these' : ''}
                  </button>
                )}
              </div>
              {/* Birthday greeting — opens by itself when someone in scope has a birthday today. */}
              <Dialog open={birthdayOpen} onOpenChange={setBirthdayOpen}>
                <DialogContent className="max-w-md" data-testid="birthday-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <span aria-hidden="true">🎂</span>
                      {birthdayPeople.length === 1 ? "Today's birthday" : `${birthdayPeople.length} birthdays today`}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-2">
                    {birthdayPeople.map((e) => {
                      const age = ageFrom(e.dob);
                      return (
                        <Link key={e.id} to={`/employees/${e.id}`} onClick={() => setBirthdayOpen(false)}
                          data-testid={`birthday-person-${e.id}`}
                          className="flex items-center gap-3 rounded-lg border border-[#E6E9F0] p-3 hover:bg-[#F7F8FB] transition-colors">
                          <div className="w-10 h-10 rounded-full bg-[#FFF3D6] text-[#8A5A12] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                            {(e.name || '?').split(' ').slice(0, 2).map((w) => w[0]).join('')}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#11182B] truncate">{e.name}</p>
                            <p className="text-[11px] text-[#5A6377] truncate">
                              {e.designation}{e.department ? ` · ${e.department}` : ''}{e.unit ? ` · ${e.unit}` : ''}
                            </p>
                            {age !== null && (
                              <p className="text-[11px] text-[#8A5A12] mt-0.5">Turning {age} today</p>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button size="sm" variant="outline" onClick={() => setBirthdayOpen(false)}>Close</Button>
                  </div>
                </DialogContent>
              </Dialog>

              {canCreateEmployee && (
                <div className="flex items-center gap-2">
                  {/* Filling a seat that is already open is the common case
                      after a resignation, and it is the one that has to link
                      the joiner to the vacancy — that link is what Requirement
                      TAT is measured from. Given its own button so it is a
                      choice HR makes up front, not a dropdown they might miss. */}
                  {replacementSeats.length > 0 && (
                    <Dialog open={vacancyDialogOpen} onOpenChange={setVacancyDialogOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="add-from-vacancy-button" variant="outline"
                                className="border-[#E6E9F0] text-[#15307F]"
                                title="Fill a seat left empty by a resignation or exit">
                          <Briefcase className="w-4 h-4 mr-2" />
                          Add from Vacancy
                          <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#E8EFFD] text-[#15307F]">
                            {replacementSeats.length}
                          </span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Add Employee from a Vacancy</DialogTitle></DialogHeader>
                        <CreateEmployeeForm units={units} departments={departments} designations={designations}
                          vacancyFirst
                          onSuccess={() => { loadData(); setVacancyDialogOpen(false); }} />
                      </DialogContent>
                    </Dialog>
                  )}
                  <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="create-employee-button">
                        <Plus className="w-4 h-4 mr-2" /> Add Employee
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>Add New Employee</DialogTitle></DialogHeader>
                      <CreateEmployeeForm units={units} departments={departments} designations={designations} onSuccess={() => { loadData(); setCreateDialogOpen(false); }} />
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>

            {/* Birthdays — today, then the next 7 days. Hidden entirely when there is nothing to show. */}
            {(birthdayPeople.length > 0 || upcomingBirthdays.length > 0) && (
              <div data-testid="upcoming-birthdays-panel" className="mb-6 bg-white border border-[#E6E9F0] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span aria-hidden="true">🎂</span>
                  <h2 className="text-sm font-semibold text-[#11182B]">Upcoming Birthdays</h2>
                  <span className="text-[11px] text-[#98A1B2]">next 7 days</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {birthdayPeople.map((e) => (
                    <Link key={e.id} to={`/employees/${e.id}`} data-testid={`birthday-today-${e.id}`}
                      className="flex items-center gap-2.5 rounded-lg border border-[#F0C97A] bg-[#FFF8E8] px-3 py-2 hover:bg-[#FFF3D6] transition-colors min-w-[220px]">
                      <div className="w-9 h-9 rounded-full bg-[#F5C86B] text-[#6B4408] flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(e.name || '?').split(' ').slice(0, 2).map((w) => w[0]).join('')}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-[#11182B] truncate">{e.name}</p>
                        <p className="text-[11px] text-[#8A5A12] font-medium">
                          Today{ageFrom(e.dob) !== null ? ` · turning ${ageFrom(e.dob)}` : ''}
                        </p>
                      </div>
                    </Link>
                  ))}

                  {upcomingBirthdays.map(({ emp: e, days }) => (
                    <Link key={e.id} to={`/employees/${e.id}`} data-testid={`birthday-upcoming-${e.id}`}
                      className="flex items-center gap-2.5 rounded-lg border border-[#E6E9F0] bg-[#FBFCFE] px-3 py-2 hover:bg-[#F7F8FB] transition-colors min-w-[220px]">
                      <div className="w-9 h-9 rounded-full bg-[#E8EFFD] text-[#15307F] flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(e.name || '?').split(' ').slice(0, 2).map((w) => w[0]).join('')}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-[#11182B] truncate">{e.name}</p>
                        <p className="text-[11px] text-[#5A6377] truncate">
                          {days === 1 ? 'Tomorrow' : `In ${days} days`}
                          {e.dob ? ` · ${new Date(e.dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                        </p>
                        <p className="text-[10px] text-[#98A1B2] truncate">{e.department}{e.unit ? ` · ${e.unit}` : ''}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Pending documents, and beside it the week-long confirmation-due alert.
                A flex row rather than a grid so the docs panel spans the full width
                on days when nobody is due for confirmation. */}
            <div className="mb-6 flex flex-col lg:flex-row items-start gap-4">
              <div data-testid="pending-docs-panel" className="flex-1 min-w-0 w-full bg-white border border-[#E6E9F0] rounded-lg p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-sm font-semibold text-[#11182B]">Pending Documents</div>
                  <select data-testid="pending-doc-type-select"
                          className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5 min-w-[220px]"
                          value={pendingDocType} onChange={(e) => setPendingDocType(e.target.value)}>
                    <option value="">Select a document type…</option>
                    {docReqs.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                    <option value="Photo">Photo</option>
                  </select>
                  {pendingDocType && (
                    <span data-testid="pending-doc-count" className="text-xs text-[#5A6377]">
                      {pendingLoading ? 'Loading…' : `${pendingList.length} employee${pendingList.length === 1 ? '' : 's'} pending`}
                    </span>
                  )}
                </div>
                {pendingDocType && !pendingLoading && (
                  pendingList.length === 0 ? (
                    <div data-testid="pending-doc-empty" className="mt-3 text-xs text-[#15A36E]">Everyone has uploaded this document.</div>
                  ) : (
                    <ul data-testid="pending-doc-list" className="mt-3 max-h-[260px] overflow-auto divide-y divide-[#F2F4F8] border border-[#F2F4F8] rounded-md">
                      {pendingList.map((e) => (
                        <li key={e.id} data-testid={`pending-doc-row-${e.id}`} className="px-3 py-2 text-sm flex items-center justify-between gap-3">
                          <Link to={`/employees/${e.id}`} className="text-[#2563EB] hover:underline flex-shrink-0">{e.name || '—'}</Link>
                          <span className="text-[11px] text-[#5A6377] flex-1 truncate">{e.department}{e.designation ? ` · ${e.designation}` : ''}</span>
                          <span className="text-[11px] text-[#98A1B2] tabular-nums">{e.card_no || '—'}</span>
                        </li>
                      ))}
                    </ul>
                  )
                )}
              </div>
              <ConfirmationDuePanel />
            </div>

            <div className="mb-6 flex gap-4 flex-wrap">
              <Input data-testid="employee-search-input" placeholder="Search by name..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs border-[#E6E9F0]" />
              <Select value={filterDept} onValueChange={setFilterDept}>
                <SelectTrigger data-testid="filter-dept-select" className="w-48 border-[#E6E9F0]"><SelectValue placeholder="Filter by Department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departmentTree(departments).map((d) => (
                    <SelectItem key={d.id} value={d.name}>
                      {d.depth ? `\u2003\u2514 ${d.name}` : d.name}{d.children?.length ? ' (all)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterSource} onValueChange={(v) => setFilterSource(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-source-select" className="w-56 border-[#E6E9F0]"><SelectValue placeholder="Filter by Source of Hiring" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {(hiringMeta?.sources || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  <SelectItem value="Not Recorded">Not Recorded</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterExp} onValueChange={(v) => setFilterExp(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-experience-select" className="w-44 border-[#E6E9F0]"><SelectValue placeholder="Filter by Experience" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Experience</SelectItem>
                  {(hiringMeta?.experience_types || ['Fresher', 'Experienced']).map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                  <SelectItem value="Not Recorded">Not Recorded</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(filterDept || filterLevel || filterJoinYear || filterDesig || filterSource || filterExp) && (
              <div data-testid="active-filters" className="mb-4 flex items-center gap-2 text-xs">
                <span className="text-[#5A6377]">Filtered:</span>
                {filterDept && <span className="px-2 py-0.5 rounded-full bg-[#E8EFFD] text-[#15307F]">Dept: {filterDept}</span>}
                {filterDesig && <span className="px-2 py-0.5 rounded-full bg-[#FBF7EC] text-[#8A5A12]">Designation: {filterDesig}</span>}
                {filterLevel && <span className="px-2 py-0.5 rounded-full bg-[#EEEAFB] text-[#7C6BD6]">Level: L{filterLevel}</span>}
                {filterJoinYear && <span className="px-2 py-0.5 rounded-full bg-[#E7F7EF] text-[#15A36E]">Joined: {filterJoinYear}</span>}
                {filterSource && <span className="px-2 py-0.5 rounded-full bg-[#EFF3FB] text-[#2F4A77]">Source: {filterSource}</span>}
                {filterExp && <span className="px-2 py-0.5 rounded-full bg-[#EFF3FB] text-[#2F4A77]">Experience: {filterExp}</span>}
                <button data-testid="clear-filters" className="text-[#E11D2A] hover:underline" onClick={() => { setFilterDept(''); setFilterLevel(''); setFilterJoinYear(''); setFilterDesig(''); setFilterSource(''); setFilterExp(''); }}>Clear</button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12"><div className="w-12 h-12 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
            ) : (
              <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#102468] text-white text-xs font-semibold tracking-wide uppercase border-b border-[#E6E9F0]">
                      <tr>
                        <SortHead label="Name" k="name" />
                        <SortHead label="Card No" k="card_no" sortable={false} />
                        <SortHead label="Unit" k="unit" sortable={false} />
                        <SortHead label="Department" k="department" />
                        <SortHead label="Designation" k="designation" />
                        <SortHead label="Source of Hiring" k="source_of_hiring" />
                        <SortHead label="Experience" k="experience_label" />
                        {showSalary && <SortHead label="Salary" k="locked_salary" />}
                        <SortHead label="DOJ" k="doj" />
                        <SortHead label="DOB" k="dob" />
                        <SortHead label="Status" k="status" sortable={false} />
                        <SortHead label="Actions" k="actions" sortable={false} />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployees.map((emp) => (
                        <tr key={emp.id} data-testid={`employee-row-${emp.id}`}
                            className={`border-b border-[#E6E9F0] last:border-0 transition-colors ${
                              onNotice(emp)
                                ? 'bg-[#FFFAF0] hover:bg-[#FDF3E3] border-l-[3px] border-l-[#E9A23B]'
                                : 'hover:bg-[#F7F8FB]'}`}>
                          <td className="py-3 px-4 text-sm text-[#11182B] font-medium">
                            {/* A dot, not a banner. Someone serving notice is
                                still an ordinary employee — the name column
                                stays a name column, and the detail lives in
                                Status where every other state is read. */}
                            <span className="flex items-center gap-2">
                              {onNotice(emp) && (
                                <span data-testid={`notice-badge-${emp.id}`}
                                      title={`Serving notice — last working day ${emp.last_working_day || 'not set'}`}
                                      className="w-1.5 h-1.5 rounded-full bg-[#E9A23B] flex-shrink-0" />
                              )}
                              <span className="truncate">{emp.name}</span>
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{emp.card_no}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{emp.unit}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{emp.department}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{emp.designation}{emp.level ? <span className="text-[10px] text-[#98A1B2] ml-1">L{emp.level}</span> : null}</td>
                          {/* Source and Experience. Both can legitimately be
                              empty — every employee already on the roll
                              predates these fields — so a blank reads as
                              "Not Recorded" rather than being guessed at. The
                              qualifier (who referred them, which agency) is a
                              tooltip: it matters when you ask, not at a
                              glance down the column. */}
                          <td className="py-3 px-4 text-sm text-[#5A6377] max-w-[190px]"
                              title={[emp.source_of_hiring, emp.source_detail].filter(Boolean).join(' — ') || 'Not recorded'}>
                            {emp.source_of_hiring ? (
                              <span className="block truncate">
                                {emp.source_of_hiring}
                                {emp.source_detail && (
                                  <span className="block text-[10px] text-[#98A1B2] truncate">{emp.source_detail}</span>
                                )}
                              </span>
                            ) : <NotRecorded />}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {emp.experience_type
                              ? <span className={emp.experience_type === 'Fresher'
                                    ? 'text-[#15654B] bg-[#EAF7F0] border border-[#CDEBDB] rounded-full px-2 py-0.5 text-xs font-medium'
                                    : 'text-[#2F4A77] bg-[#EFF3FB] border border-[#D9E2F3] rounded-full px-2 py-0.5 text-xs font-medium'}>
                                  {emp.experience_label}
                                </span>
                              : <NotRecorded />}
                          </td>
                          {showSalary && (
                            <td className="py-3 px-4 text-sm text-[#11182B] font-medium tabular-nums whitespace-nowrap">
                              {inrFmt(emp.locked_salary)}
                              {emp.locked_salary_year ? <span className="text-[10px] text-[#98A1B2] ml-1">{emp.locked_salary_year}</span> : null}
                              {emp.locked_salary_proposed ? <span className="text-[9px] font-medium text-[#E9A23B] bg-[#FBF7EC] rounded-full px-1.5 py-0.5 ml-1 align-middle">proposed</span> : null}
                            </td>
                          )}
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{emp.doj}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">
                            {emp.dob || <span className="text-[#C9CFDB]">—</span>}
                          </td>
                          <td className="py-3 px-4">
                            {/* One derived state, served by the backend, so
                                this badge can never disagree with the profile
                                or the org chart. */}
                            <span data-testid={`employee-state-${emp.id}`}
                                  className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                    onNotice(emp) ? 'bg-[#FBEAD3] text-[#8A5A12]'
                                      : (emp.employment_state || emp.status) === 'Active' ? 'bg-[#E4F6EE] text-[#15A36E]'
                                      : 'bg-[#FDE7E9] text-[#E11D2A]'}`}>
                              {onNotice(emp) ? 'On Notice' : (emp.employment_state || emp.status)}
                            </span>
                            {onNotice(emp) && emp.last_working_day && (
                              <div data-testid={`notice-lwd-${emp.id}`}
                                   className="text-[10px] leading-tight text-[#8A5A12] mt-1 whitespace-nowrap">
                                Last day {dmy(emp.last_working_day)}
                                {emp.notice_days_left != null && emp.notice_days_left >= 0
                                  ? ` · ${emp.notice_days_left}d` : ''}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <Link to={`/employees/${emp.id}`} title="View profile">
                                <Button data-testid={`view-employee-${emp.id}`} variant="ghost" size="sm" className="text-[#2563EB] hover:bg-[#EFF1F6]"><Eye className="w-4 h-4" /></Button>
                              </Link>
                              {/* View and Edit only. Resign is deliberately NOT
                                  a row action: an exit carries dates, a
                                  mandatory reason and a vacancy behind it, and
                                  one stray click beside Edit on a long list is
                                  the wrong way to reach it. It lives on the
                                  employee's own profile, where whoever is doing
                                  it is already looking at the person. The
                                  endpoint and the dialog are unchanged. */}
                              {hasPerm('employee.edit') && (
                                <Link to={`/employees/${emp.id}?edit=1`} title="Edit details">
                                  <Button data-testid={`edit-employee-${emp.id}`} variant="ghost" size="sm" className="text-[#5A6377] hover:bg-[#EFF1F6]"><Pencil className="w-4 h-4" /></Button>
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

    </div>
  );
}

function CreateEmployeeForm({ units, departments, designations, onSuccess, vacancyFirst = false }) {
  const hiringMeta = useHiringMeta();
  const { activeUnit, isAllUnits } = useUnit();
  const LOGO_URL = (typeof window !== 'undefined' ? window.location.origin : '') + '/assets/aampl-logo.jpg';
  const [formData, setFormData] = useState({ name: '', card_no: '', father_husband: '', unit: isAllUnits ? '' : (activeUnit || ''), department: '', designation: '', category: '', doj: '', dob: '', email: '', mobile: '', education: '', year_passing: '', joining_salary: '', reporting_to: '',
    source_of_hiring: '', source_detail: '', experience_type: '', prior_experience_years: '' });
  const [step, setStep] = useState(1);
  const [reqs, setReqs] = useState([]);
  const [files, setFiles] = useState({});
  const [managers, setManagers] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [vacancyId, setVacancyId] = useState('');
  const [hired, setHired] = useState(false);
  // Once HR unticks it themselves, stop deciding for them.
  const [hiredTouched, setHiredTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scanFor, setScanFor] = useState(null);   // requirement name currently being scanned

  useEffect(() => { docReqApi.list().then((r) => setReqs(r.data || [])).catch(() => setReqs([])); }, []);
  // Scoped to the workspace you are in. Without the unit param the backend
  // hands a full-scope admin every unit's seats, so someone working in Unit 3
  // was offered Unit 1 vacancies and could hire across plants by accident.
  useEffect(() => {
    vacancyApi.list(isAllUnits ? {} : { unit: activeUnit })
      .then((r) => setVacancies((r.data || []).filter((v) => v.status === 'Open')))
      .catch(() => setVacancies([]));
  }, [activeUnit, isAllUnits]);
  // Keep Unit in sync with the active workspace: pre-fill for single-unit scoped users
  // and full-scope admins in a specific unit; leave empty in All-Units mode.
  useEffect(() => {
    const u = isAllUnits ? '' : (activeUnit || '');
    setFormData((s) => (s.unit === u ? s : { ...s, unit: u, reporting_to: '' }));
    if (u) loadManagers(u, formData.designation);
    // eslint-disable-next-line
  }, [activeUnit, isAllUnits]);

  const desigLevel = (name) => { const d = designations.find((x) => x.name === name); return d && d.level != null ? d.level : undefined; };

  const loadManagers = (unit, designation, preselect) => {
    if (!unit) { setManagers([]); return Promise.resolve([]); }
    const lvl = desigLevel(designation);
    const params = { unit };
    if (lvl != null) params.for_level = lvl;
    return employeeApi.managers(params)
      .then(async (r) => {
        let list = r.data || [];
        // The manager the vacancy already names must always be selectable, even
        // where the seniority filter would have hidden them. Otherwise Reports
        // To silently falls back to empty and HR re-picks a manager the system
        // already knew.
        if (preselect && !list.some((m) => m.id === preselect)) {
          try {
            const all = await employeeApi.managers({ unit });
            const found = (all.data || []).find((m) => m.id === preselect);
            if (found) list = [found, ...list];
          } catch (e) { /* keep the filtered list */ }
        }
        setManagers(list);
        return list;
      })
      .catch(() => { setManagers([]); return []; });
  };

  const pickVacancy = (vid) => {
    if (!vid || vid === '__none__') { setVacancyId(''); return; }
    setVacancyId(vid);
    const v = vacancies.find((x) => x.id === vid);
    if (!v) return;
    setFormData((s) => ({ ...s, unit: v.unit, department: v.department, designation: v.title, reporting_to: '' }));
    // Reports To is set only once the manager list is actually in hand. A
    // <Select> whose value matches no mounted option renders as the empty
    // placeholder — which is exactly why the auto-filled manager looked as
    // though it had never been picked.
    loadManagers(v.unit, v.title, v.reporting_to).then((list) => {
      if (v.reporting_to && list.some((m) => m.id === v.reporting_to)) {
        setFormData((s) => ({ ...s, reporting_to: v.reporting_to }));
      }
    });
  };

  const basePayload = () => ({
    ...formData,
    joining_salary: formData.joining_salary === '' ? 0 : parseFloat(formData.joining_salary),
    year_passing: formData.year_passing ? parseInt(formData.year_passing) : null,
    reporting_to: formData.reporting_to || null,
    // Blank means NOT RECORDED and must reach the server as null, not "" —
    // an empty string would be validated as a bad value instead of an absent
    // one and reject the whole form.
    source_of_hiring: formData.source_of_hiring || null,
    source_detail: formData.source_detail || null,
    experience_type: formData.experience_type || null,
    prior_experience_years: formData.experience_type
      ? (formData.prior_experience_years === '' ? null : Number(formData.prior_experience_years))
      : null,
  });

  const mandatory = reqs.filter((r) => r.mandatory);
  const missingMandatory = mandatory.filter((r) => !files[r.name]);

  const step1Submit = async (e) => {
    e.preventDefault();
    if (vacancyFirst && !vacancyId) { toast.error('Pick the vacancy this person is filling'); return; }
    if (!formData.unit || formData.unit === ALL_UNITS) { toast.error('Pick a unit for this employee'); return; }
    if (hired) { await createPreJoiner(); } else { setStep(2); }
  };

  const chosenVacancy = vacancies.find((v) => v.id === vacancyId) || null;

  // A joining date in the future means exactly one thing: they have not joined
  // yet. Leaving the box unticked would create them as a live employee from
  // today — counted in headcount, drawn on the org chart and issued a KPI
  // sheet weeks before they walk in. So the date decides the box, rather than
  // HR having to remember the box after setting the date.
  const joinsInFuture = (() => {
    if (!formData.doj) return false;
    const today = new Date().toLocaleDateString('en-CA');   // local YYYY-MM-DD
    return formData.doj > today;
  })();
  useEffect(() => {
    // Only while HR has not expressed their own preference.
    if (!hiredTouched) setHired(joinsInFuture);
  }, [joinsInFuture, hiredTouched]);

  // "Add Employee from a Vacancy" is about REPLACING somebody. So it offers
  // only the seats a departure actually emptied — the ones that carry the
  // name of the person who left. A position opened from scratch has nobody
  // behind it and nothing to replace, and belongs on the ordinary Add
  // Employee form instead.
  const replacementVacancies = vacancies.filter((v) => v.from_employee_name);
  const pickable = vacancyFirst ? replacementVacancies : vacancies;

  const createPreJoiner = async () => {
    setLoading(true);
    try {
      const res = await preJoinerApi.create({ ...basePayload(), vacancy_id: vacancyId || null });
      const id = res?.data?.id;
      toast.success('Pre-joiner added — opening offer letter');
      try { const lr = await preJoinerApi.offerLetter(id, { logo_url: LOGO_URL }); const w = window.open('', '_blank'); if (w) { w.document.write(lr.data.html); w.document.close(); } } catch (e2) { /* optional */ }
      onSuccess();
    } catch (error) { toast.error(error?.response?.data?.detail || 'Failed to add pre-joiner'); }
    finally { setLoading(false); }
  };

  const createEmployee = async () => {
    if (missingMandatory.length) { toast.error(`Upload required: ${missingMandatory.map((r) => r.name).join(', ')}`); return; }
    setLoading(true);
    try {
      const res = await employeeApi.create({ ...basePayload(), fill_vacancy_id: vacancyId || null });
      const id = res?.data?.id;
      const chosen = Object.entries(files).filter(([, f]) => f);
      let failed = [];
      for (const [name, f] of chosen) { try { await documentApi.upload(id, name, f); } catch (err) { failed.push(name); } }
      if (failed.length) toast.error(`Employee created, but these uploads failed: ${failed.join(', ')} — finish them on the profile.`);
      else toast.success('Employee created with documents');
      onSuccess();
    } catch (error) { toast.error(error?.response?.data?.detail || 'Failed to create employee'); }
    finally { setLoading(false); }
  };

  // Nothing to replace. Said plainly rather than showing an empty picker the
  // form then refuses to submit without.
  if (vacancyFirst && replacementVacancies.length === 0) {
    return (
      <div data-testid="no-replacement-vacancies" className="py-8 text-center">
        <p className="text-sm text-[#11182B] font-medium">Nobody&apos;s seat is waiting to be filled.</p>
        <p className="text-xs text-[#5A6377] mt-2 max-w-sm mx-auto">
          This list only holds positions emptied by a resignation or an exit. A seat appears here
          the moment a resignation is recorded. For a brand-new position, use <b>Add Employee</b>.
        </p>
      </div>
    );
  }

  if (step === 1) {
    return (
      <form onSubmit={step1Submit} className="space-y-4">
        {/* The seat picker belongs to ONE route in, not two. Asking "Filling an
            open vacancy?" on the plain Add Employee form duplicated the Add
            from Vacancy button sitting right beside it, and the two then
            disagreed about what they offered — the button lists only seats
            somebody vacated, the dropdown listed every open position. So each
            button now does one job: Add Employee opens a NEW position, Add
            from Vacancy fills an existing one and arrives with that already
            settled. */}
        {vacancyFirst && pickable.length > 0 && (
          <div className="bg-[#F1F5FD] border border-[#DBE6FB] rounded-lg p-3">
            <Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">
              Whose place is this person taking? <span className="text-[#E11D2A]">*</span>
            </Label>
            <Select value={vacancyId || '__none__'} onValueChange={pickVacancy}>
              <SelectTrigger data-testid="pick-vacancy-select">
                <SelectValue placeholder="Select the person being replaced" />
              </SelectTrigger>
              <SelectContent>
                {pickable.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {/* Name first — HR is looking for the person who left,
                        not for a job title. */}
                    {v.from_employee_name
                      ? `${v.from_employee_name} — ${v.title} · ${v.department} · ${v.unit}`
                      : `${v.title} — ${v.department} · ${v.unit}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#5A6377] mt-1.5">
              Only seats emptied by a resignation or exit are listed here — {pickable.length} of them.
              For a brand-new position use <b>Add Employee</b> instead.
            </p>
            {chosenVacancy ? (
              <div data-testid="vacancy-context" className="text-[11px] text-[#15307F] mt-2 space-y-0.5">
                <p>
                  Unit, department, designation and reporting manager are taken from the vacancy —
                  nothing to re-enter, and the new joiner lands in the right place on the Org Chart.
                </p>
                {chosenVacancy.from_employee_name && (
                  <p>
                    Replacing <b>{chosenVacancy.from_employee_name}</b>
                    {chosenVacancy.last_working_day ? ` (last working day ${chosenVacancy.last_working_day})` : ''}.
                  </p>
                )}
                <p className="text-[#15A36E]">
                  Recruitment TAT is calculated automatically from{' '}
                  {chosenVacancy.vacancy_created_date || 'the day the seat opened'} to the Date of Joining below.
                </p>
              </div>
            ) : (
              <p className="text-[11px] text-[#5A6377] mt-2">
                Pick the seat first. Everything the vacancy already knows is filled in for you.
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Name *</Label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Card No *</Label><Input required value={formData.card_no} onChange={(e) => setFormData({ ...formData, card_no: e.target.value })} /></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Father/Husband</Label><Input value={formData.father_husband} onChange={(e) => setFormData({ ...formData, father_husband: e.target.value })} /></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Unit *</Label>
            {isAllUnits ? (
              <Select required value={formData.unit} onValueChange={(v) => { setFormData({ ...formData, unit: v, reporting_to: '' }); loadManagers(v, formData.designation); }}>
                <SelectTrigger data-testid="create-emp-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input data-testid="create-emp-unit-locked" value={formData.unit} disabled className="bg-[#F7F8FB] text-[#5A6377] cursor-not-allowed" />
            )}
          </div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Department *</Label>
            <Select required value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
              <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>
                {departmentTree(departments).map((d) => (
                  <SelectItem key={d.id} value={d.name}>{d.depth ? `\u2003\u2514 ${d.name}` : d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Designation *</Label>
            <Select required value={formData.designation} onValueChange={(v) => { setFormData({ ...formData, designation: v }); loadManagers(formData.unit, v); }}>
              <SelectTrigger><SelectValue placeholder="Select designation" /></SelectTrigger>
              <SelectContent>{designations.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Reports To</Label>
            <Select value={formData.reporting_to} onValueChange={(v) => setFormData({ ...formData, reporting_to: v })}>
              <SelectTrigger><SelectValue placeholder={formData.unit ? 'Select manager' : 'Pick a unit first'} /></SelectTrigger>
              <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {m.designation}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">DOJ *</Label><Input required type="date" value={formData.doj} onChange={(e) => setFormData({ ...formData, doj: e.target.value })} /></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Date of Birth</Label><Input data-testid="create-emp-dob" type="date" value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} /></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Email <span className="font-normal text-[#98A1B2]">(for wishes &amp; portal login)</span></Label><Input data-testid="create-emp-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="name@example.com" /></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Contact Number <span className="font-normal text-[#98A1B2]">(10-digit mobile)</span></Label><Input data-testid="create-emp-mobile" type="tel" inputMode="numeric" maxLength={10} value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="9876543210" /></div>
          <div><Label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Joining Salary {hired ? '(optional — not printed on the offer letter)' : '*'}</Label><Input required={!hired} type="number" value={formData.joining_salary} onChange={(e) => setFormData({ ...formData, joining_salary: e.target.value })} /></div>
          {/* Optional, like the rest of this block: recording where a hire
              came from should never be what blocks adding them. */}
          <SourceOfHiringFields meta={hiringMeta} value={formData} idPrefix="create"
                                onChange={(patch) => setFormData((p) => ({ ...p, ...patch }))} />
          <ExperienceFields meta={hiringMeta} value={formData} idPrefix="create"
                            onChange={(patch) => setFormData((p) => ({ ...p, ...patch }))} />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm text-[#11182B] cursor-pointer">
            <input data-testid="create-emp-hired" type="checkbox" checked={hired}
                   onChange={(e) => { setHiredTouched(true); setHired(e.target.checked); }} />
            Hired, not joined yet (issue an offer letter; documents collected at joining)
          </label>
          {joinsInFuture && hired && (
            <p data-testid="future-doj-note" className="text-[11px] text-[#15A36E] mt-1 ml-6">
              Ticked for you — {dmy(formData.doj)} is still ahead, so they go to Pre-joining and
              become an employee on the day they actually start.
            </p>
          )}
          {joinsInFuture && !hired && (
            <p data-testid="future-doj-warning" className="text-[11px] text-[#B14A0E] mt-1 ml-6">
              {dmy(formData.doj)} is in the future. Left unticked, they are created as a live
              employee today — counted in headcount and drawn on the Org Chart before they join.
            </p>
          )}
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-[#2563EB] hover:bg-[#C2151F]">
          {hired ? (loading ? 'Adding…' : 'Add Pre-joiner & Generate Offer Letter') : 'Next: Documents →'}
        </Button>
      </form>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#11182B]">Upload joining documents</h3>
        <button type="button" onClick={() => setStep(1)} className="text-xs text-[#2563EB]">← Back to details</button>
      </div>
      <p className="text-xs text-[#5A6377]">Documents marked <span className="text-[#C2151F]">*</span> are mandatory and must be attached before the employee can be created. The rest can be added later from the profile.</p>
      <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
        {reqs.map((r) => (
          <div key={r.id} className="flex items-center gap-3 text-sm py-1 border-b border-[#F2F4F8] last:border-0">
            <span className="flex-1 text-[#11182B]">{r.name}{r.mandatory ? <span className="text-[#C2151F]"> *</span> : null}</span>
            {files[r.name] ? <span className="text-[11px] text-[#15A36E] truncate max-w-[130px]">{files[r.name].name}</span> : null}
            <label data-testid={`doc-file-${r.name}`} className="text-xs text-[#2563EB] cursor-pointer hover:underline">
              {files[r.name] ? 'Change' : 'Choose file'}
              <input type="file" className="hidden" onChange={(ev) => { const f = ev.target.files && ev.target.files[0]; setFiles((s) => ({ ...s, [r.name]: f || undefined })); ev.target.value = ''; }} />
            </label>
            {/* Scan: opens the DocumentScanner (live camera + jscanify/OpenCV
                edge-detect & perspective-warp). Feeds the SAME `files` state as
                the gallery picker. */}
            <button type="button"
                    data-testid={`doc-scan-${r.name}`}
                    onClick={() => setScanFor(r.name)}
                    className="text-xs text-[#2563EB] cursor-pointer hover:underline bg-transparent border-0 p-0">
              Scan
            </button>
          </div>
        ))}
        {reqs.length === 0 && <p className="text-xs text-[#98A1B2]">No document requirements configured.</p>}
      </div>
      {missingMandatory.length > 0 && <p className="text-xs text-[#C2151F]">Still required: {missingMandatory.map((r) => r.name).join(', ')}</p>}
      <Button onClick={createEmployee} disabled={loading || missingMandatory.length > 0} className="w-full bg-[#2563EB] hover:bg-[#C2151F] disabled:opacity-50">{loading ? 'Creating…' : 'Create Employee'}</Button>
      <DocumentScanner
        open={!!scanFor}
        docName={scanFor || ''}
        onCancel={() => setScanFor(null)}
        onCapture={(file) => { setFiles((s) => ({ ...s, [scanFor]: file })); setScanFor(null); }}
      />
    </div>
  );
}
